// Packages
import React, { useCallback, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';

// Components
import { TcotableValues } from 'types/TcoProducts';
import './BarChart.scss';
import { tcoFormattedPrice } from 'utils/defaultsAndConstants/tcoConstants';
import { useTcoDashboardExports } from 'hooks/useTcoDashboardExports';
import ContextMenu from '../ContextMenu';
import LegalDisclaimers from '../LegalDisclaimers';
import {
  monthlyCostDescription,
  monthlyCostLabels,
  oneTimeCostDescription,
  oneTimeCostLabels,
} from 'utils/defaultsAndConstants/constants';
import { Toast } from '@kite/react-kite-plus';
import { TToast } from 'types';

interface CostSectionProps {
  title: string;
  labels: string[];
  description: string[];
  costValues: {
    spectrum: number[];
    diy: number[];
  };
  totalCost: {
    spectrum: number;
    diy: number;
  };
}

export interface IBarChartProps {
  totalOutput: TcotableValues<number>;
  isOneTime: boolean;
  miscItHour?: number;
  spectrumOneTimeCost: number;
  spectrumOngoingMonthlyCost: number;
  diyOneTimeCost: number;
  diyOngoingMonthlyCost: number;
}

const BarChart = ({
  totalOutput,
  isOneTime,
  miscItHour,
  spectrumOneTimeCost,
  spectrumOngoingMonthlyCost,
  diyOneTimeCost,
  diyOngoingMonthlyCost,
}: IBarChartProps) => {
  // =============================================
  // Helpers (Memo, CB, vars)
  // =============================================

  const mainRef = useRef<HTMLDivElement>(null);
  const [toast, setToast] = useState<TToast>(null);
  const { downloadChartAsImage, copyChartToClipBoard, handleChartPdf } =
    useTcoDashboardExports(mainRef);

  const spectrumOneTimeCostValues = [
    totalOutput.spectrumEquipmentCosts,
    totalOutput.spectrumCostOfCapital,
    totalOutput.spectrumImplementationCosts,
    totalOutput.spectrumTraining,
  ];

  const diyOneTimeCostValues = [
    totalOutput.diyEquipmentCosts,
    totalOutput.diyCostOfCapital,
    totalOutput.diyImplementationCosts,
    totalOutput.diyTraining,
  ];

  const spectrumMonthlyCostValues = [
    totalOutput.spectrumMonthlyDeviceManagementFee,
    totalOutput.spectrumConfigurationCostTotal,
    totalOutput.spectrumMonitoringCostTotal,
    totalOutput.spectrumTroubleshootingCostTotal,
    0,
    0,
  ];

  const diyMonthlyCostValues = [
    totalOutput.diyMonthlyDeviceManagementFee,
    totalOutput.diyConfigurationCostTotal,
    totalOutput.diyMonitoringCostTotal,
    totalOutput.diyTroubleshootingCostTotal,
    totalOutput.licenseManagementFee,
    miscItHour!,
  ];

  const data = {
    // Name of the variables on x-axies for each bar
    labels: isOneTime ? oneTimeCostLabels : monthlyCostLabels,
    datasets: [
      {
        // Label for bars
        label: 'Spectrum',
        // Data or value of your each variable
        data: isOneTime ? spectrumOneTimeCostValues : spectrumMonthlyCostValues,
        // Color of each bar
        backgroundColor: '#0271EB',
        // Border color of each bar
        borderColor: '0271EB',
        borderWidth: 0.5,
        barThickness: 30,
        categoryPercentage: 0.8,
        barPercentage: 0.6,
      },
      {
        // Label for bars
        label: 'DIY',
        // Data or value of your each variable
        data: isOneTime ? diyOneTimeCostValues : diyMonthlyCostValues,
        // Color of each bar
        backgroundColor: '#002139',
        // Border color of each bar
        borderColor: '002139',
        borderWidth: 0.5,
        barThickness: 30,
        categoryPercentage: 0.8,
        barPercentage: 0.6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 2,
    scales: {
      xAxes: [
        {
          gridLines: {
            display: false, // Hides the vertical grid lines
            color: 'red',
          },
          ticks: {
            fontStyle: 400,
            fontSize: 12,
            fontColor: '#000',
            // maxRotation: 0,     //for keeping the labels horizontal
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            // The y-axis value will start from zero
            beginAtZero: true,
            callback: function (value: number) {
              return '$' + value?.toLocaleString();
            },
            fontStyle: 400,
            fontSize: 12,
            fontColor: '#000',
          },
        },
      ],
    },
    tooltips: {
      mode: 'index',
      callbacks: {
        label: function (tooltipItem: any, data: any) {
          let label =
            data.datasets?.[tooltipItem.datasetIndex || 0]?.label || '';

          return ` ${label} : $${tooltipItem.yLabel.toLocaleString()}`;
        },
      },
    },
    legend: {
      labels: {
        fontSize: 12,
        fontColor: '#000',
      },
      display: true,
      position: 'bottom',
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  const renderCostSection = useCallback(
    ({
      title,
      labels,
      description,
      costValues,
      totalCost,
    }: CostSectionProps) => (
      <div className="dashboard bar-graph-section">
        <div className="dashboard-graph">
          <Bar data={data} options={options} />
        </div>
        <div className="dashboard-table">
          <table>
            <thead>
              <tr>
                <td className="dashboard-table-label">Type of Cost</td>
                <td>
                  <div className="dashboard-table-title">
                    <span className="circle"></span>
                    <span>Spectrum</span>
                  </div>
                </td>
                <td>
                  <div className="dashboard-table-title">
                    <span className="circle diy"></span>
                    <span>DIY</span>
                  </div>
                </td>
              </tr>
            </thead>
            <tbody>
              {labels.map((label, index) => (
                <>
                  <tr key={index}>
                    <td className="dashboard-table-label">
                      {label}
                      <br />
                      <span className="dashboard-table-label-description">
                        {description[index]}
                      </span>
                    </td>
                    <td>${tcoFormattedPrice(costValues.spectrum[index])}</td>
                    <td>${tcoFormattedPrice(costValues.diy[index])}</td>
                  </tr>
                </>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="dashboard-table-label">{title}</td>
                <td>${tcoFormattedPrice(totalCost.spectrum)}</td>
                <td>${tcoFormattedPrice(totalCost.diy)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    ),
    [data, options]
  );

  // =============================================
  // Return
  // =============================================

  const monthlyCostCheck = isOneTime
    ? spectrumOneTimeCost === 0 && diyOneTimeCost === 0
    : spectrumOngoingMonthlyCost === 0 && diyOngoingMonthlyCost === 0;

  const dataId = isOneTime ? 'one-time' : 'monthly';

  return (
    <>
      <Toast newToast={toast} uniqueKey="text" autoClose={3000} />
      <div className="kite-card bar-graph" ref={mainRef} id={dataId}>
        <div className="bar-graph-header">
          <h2 className="title">
            {isOneTime ? 'One-Time Costs' : 'Monthly Costs'}
          </h2>

          {!monthlyCostCheck && (
            <div id="context-section">
              <ContextMenu>
                <li
                  onClick={() => {
                    copyChartToClipBoard(setToast, dataId);
                  }}
                >
                  Copy Chart to Clipboard
                </li>
                <li
                  onClick={() => {
                    downloadChartAsImage(
                      isOneTime ? 'One-Time Costs' : 'Monthly Costs',
                      dataId
                    );
                  }}
                >
                  Download as Image
                </li>
                <li
                  onClick={() => {
                    handleChartPdf(
                      isOneTime ? 'One-Time Costs' : 'Monthly Costs'
                    );
                  }}
                >
                  Download as PDF
                </li>
              </ContextMenu>
            </div>
          )}
        </div>
        {isOneTime ? (
          spectrumOneTimeCost === 0 && diyOneTimeCost === 0 ? (
            <p>No data to display.</p>
          ) : (
            renderCostSection({
              title: 'Total One-Time Costs',
              labels: oneTimeCostLabels,
              description: oneTimeCostDescription,
              costValues: {
                spectrum: spectrumOneTimeCostValues,
                diy: diyOneTimeCostValues,
              },
              totalCost: {
                spectrum: spectrumOneTimeCost,
                diy: diyOneTimeCost,
              },
            })
          )
        ) : spectrumOngoingMonthlyCost === 0 && diyOngoingMonthlyCost === 0 ? (
          <p>No data to display.</p>
        ) : (
          renderCostSection({
            title: 'Total Ongoing Monthly Costs',
            labels: monthlyCostLabels,
            description: monthlyCostDescription,
            costValues: {
              spectrum: spectrumMonthlyCostValues,
              diy: diyMonthlyCostValues,
            },
            totalCost: {
              spectrum: spectrumOngoingMonthlyCost,
              diy: diyOngoingMonthlyCost,
            },
          })
        )}
        <LegalDisclaimers />
      </div>
    </>
  );
};

export default BarChart;
